/* Copyright (c) 2017 Backpack Health LLC */

import moment from 'moment';
import * as _ from 'lodash';

function setLocale(locale) {
  moment.locale(locale);
}

function subtract(date, amount, unit) {
  return moment(date).add(-amount, unit).format('YYYY-MM-DD');
}

function add(date, amount, unit) {
  return moment(date).add(amount, unit).format('YYYY-MM-DD');
}

function convertPartsToInternalFormat(year, month, day) {
  const mDate = moment();
  const hasYear = !!year;
  const hasMonth = !!month;
  const hasDay = !!day;
  let precision = '';

  if (hasYear && hasMonth && hasDay) {
    precision = 'P1D';
    mDate.set('year', year);
    mDate.set('month', month - 1);
    mDate.set('date', day);
  } else if (hasYear && hasMonth) {
    precision = 'P1M';
    mDate.set('year', year);
    mDate.set('month', month - 1);
    mDate.set('date', 1);
  } else if (hasYear) {
    precision = 'P1Y';
    mDate.set('year', year);
    mDate.set('month', 0);
    mDate.set('date', 1);
  } else {
    return {
      date: '',
      precision,
    };
  }

  return {
    date: mDate.format('YYYY-MM-DD'),
    precision,
  };
}

function breakIntoParts(date, precision) {
  if (!date) {
    return {
      year: '',
      month: '',
      day: '',
    };
  }

  const mDate = moment(date);

  let year = mDate.year();
  let month = mDate.month();
  let day = mDate.date();

  switch (precision) {
    case 'P1D':
    case 'P1DT':
      month++;
      break;
    case 'P1M':
      day = '';
      month++;
      break;
    case 'P1Y':
      day = '';
      month = '';
      break;
    default:
      day = '';
      month = '';
      year = '';
      break;
  }

  return {
    year,
    month,
    day,
  };
}

function ageData(dateOfBirth, todayDate = null) {
  const mDate = moment(dateOfBirth, 'YYYY-MM-DD', 'en', true);
  const mToday = todayDate ? moment(todayDate) : moment();
  const hours = mToday.diff(mDate, 'hours');
  let days = mToday.diff(mDate, 'days');
  if (hours < 0 && days === 0) {
    days = -1;
  } else if (days < 0) {
    days -= 1;
  }
  return {
    years: mToday.diff(mDate, 'years'),
    months: mToday.diff(mDate, 'months'),
    days,
  };
}

function getMonthOptions(isFuzzy) {
  const monthOptions = moment.months().map((m) => ({
    label: m,
    value: parseInt(moment().month(m).format('M'), 10),
  }));
  monthOptions.unshift({
    label: '--',
    value: '',
    isDisabled: !isFuzzy,
  });

  return monthOptions;
}

function getYearOptions(earliestDate, latestDate) {
  const yearOptions = [];
  const startYear = moment(earliestDate).year();
  const endYear = moment(latestDate).year();

  for (let i = endYear; i >= startYear; i--) {
    yearOptions.push({
      label: i,
      value: i,
    });
  }

  return yearOptions;
}

function getDayOptions(year, month, isFuzzy) {
  const dayOptions = [{
    label: '--',
    value: '',
    isDisabled: !isFuzzy,
  }];

  let dayCount = moment({ year, month: month - 1 }).daysInMonth();
  if (_.isNaN(dayCount)) {
    dayCount = 31;
  }

  for (let i = 1; i <= dayCount; i++) {
    dayOptions.push({
      label: i,
      value: i,
    });
  }

  return dayOptions;
}

function insureValidDateData(year, month, day, unitChanged, earliestDate, latestDate) {
  let correctedYear = year;
  let correctedMonth = month;
  const correctedDay = !day ? 1 : day;

  if (!month) {
    if (!day || unitChanged === 'month') {
      return {
        year: correctedYear,
        month: '',
        day: '',
      };
    } else if (unitChanged === 'day') {
      correctedMonth = 1;
    }
  }

  let mDate = moment({ year: correctedYear, month: correctedMonth - 1, date: correctedDay });
  let lastDayInMonth = moment({ year: correctedYear, month: correctedMonth - 1 }).add(1, 'months').date(0).date();
  if (!mDate.isValid()) {
    mDate = moment({ year: correctedYear, month: correctedMonth - 1, date: lastDayInMonth });
  }

  const mEarlyDate = moment(earliestDate).format('YYYY-MM-DD');
  const mLateDate = moment(latestDate).format('YYYY-MM-DD');

  if (mDate.isBefore(mEarlyDate)) {
    correctedYear++;
  } else if (mDate.isAfter(mLateDate)) {
    correctedYear--;
  }

  mDate = moment({ year: correctedYear, month: correctedMonth - 1, date: correctedDay });
  lastDayInMonth = moment({ year: correctedYear, month: correctedMonth - 1 }).add(1, 'months').date(0).date();
  if (!mDate.isValid()) {
    mDate = moment({ year: correctedYear, month: correctedMonth - 1, date: lastDayInMonth });
  }

  return {
    year: mDate.year(),
    month: month ? mDate.month() + 1 : '',
    day: !day ? '' : mDate.date(),
  };
}

function today() {
  return moment().format('YYYY-MM-DD');
}

function yesterday() {
  return moment().add(-1, 'days').format('YYYY-MM-DD');
}

function tomorrow() {
  return moment().add(1, 'days').format('YYYY-MM-DD');
}

function isBefore(date1, date2) {
  const mDate1 = moment(date1);
  const mDate2 = moment(date2);
  return mDate1.isBefore(mDate2);
}

function isToday(date1, forceDate) {
  const todayDate = forceDate || today();
  return moment(date1).format('YYYY-MM-DD') === todayDate;
}

function isYesterday(date1, todayDate) {
  const yesterdayDate = todayDate ? subtract(todayDate, 1, 'day') : yesterday();
  return moment(date1).format('YYYY-MM-DD') === yesterdayDate;
}

function isTomorrow(date1, todayDate) {
  const tomorrowDate = todayDate ? add(todayDate, 1, 'day') : tomorrow();
  return moment(date1).format('YYYY-MM-DD') === tomorrowDate;
}

function isInFuture(date1) {
  return moment(date1).isAfter(moment(today()));
}

function timeAgo(date) {
  return moment(date).fromNow();
}

function diff(laterDate, earlierDate) {
  const dateDiff = moment(laterDate).diff(moment(earlierDate));
  const duration = moment.duration(dateDiff);
  return duration.humanize();
}

function getMaxEndDate(endDateData) {
  const mEndDate = moment(endDateData.date);

  if (endDateData.precision === 'P1Y') {
    mEndDate.add(1, 'year');
    mEndDate.add(-1, 'day');
  } else if (endDateData.precision === 'P1M') {
    mEndDate.add(1, 'month');
    mEndDate.add(-1, 'day');
  }

  return mEndDate.format('YYYY-MM-DD');
}

function fuzzyDateDurationData(startDateData, endDateData) {
  const mEndDate = moment(endDateData.date);
  const mStartDate = moment(startDateData.date);
  const isSameFuzzy = startDateData.precision === endDateData.precision;
  const isExact = isSameFuzzy && startDateData.precision === 'P1D';

  const displayDurations = {
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    isExact,
    isRounded: false,
    isSameFuzzy,
  };

  const fuzzyRounding = (num) => {
    displayDurations.isRounded = true;
    if (isSameFuzzy) {
      return Math.round(num);
    } else {
      return num % 1 < 0.01 ? Math.floor(num) : Math.ceil(num);
    }
  };

  const roundToNearestHalf = (num) => {
    const numRemainder = num % 1;
    displayDurations.isRounded = true;

    if (isSameFuzzy) {
      return (num * 2).toFixed() / 2;
    } else if (numRemainder > 0) {
      if (numRemainder < 0.5) {
        return Math.floor(num) + 0.5;
      } else {
        return Math.ceil(num);
      }
    } else {
      return num;
    }
  };

  if (endDateData.precision === 'P1Y') {
    mEndDate.add(1, 'year');
    mEndDate.add(-1, 'day');
  } else if (endDateData.precision === 'P1M') {
    mEndDate.add(1, 'month');
    mEndDate.add(-1, 'day');
  }

  const dateDiff = mEndDate.diff(mStartDate, 'days');
  const duration = moment.duration(dateDiff, 'days');
  duration.add(1, 'days');

  const dayDiff = Math.abs(duration.asDays());
  const weekDiff = Math.abs(duration.asWeeks());
  const monthDiff = Math.abs(duration.asMonths());
  const yearDiff = dayDiff / 365;

  if (yearDiff > 1) {
    let y = Math.floor(yearDiff);
    let m = fuzzyRounding(monthDiff % 12);
    if (m === 12) {
      y++;
      m = 0;
    }

    if (yearDiff > 10) {
      y = fuzzyRounding(yearDiff);
      m = 0;
    } else if (yearDiff >= 5) {
      y = roundToNearestHalf(yearDiff);
      m = 0;
    }

    displayDurations.years = y;
    displayDurations.months = m;
  } else if (weekDiff > 10) {
    displayDurations.months = roundToNearestHalf(monthDiff);
  } else if (dayDiff > 15) {
    let w = 0;
    let m = 0;
    const wholeWeek = Math.floor(weekDiff);
    if (wholeWeek % 4 === 0) {
      if (wholeWeek === weekDiff) {
        m = wholeWeek / 4;
        w = 0;
      } else if (moment(mStartDate).startOf('month').date() === mStartDate.date() && moment(mEndDate).endOf('month').date() === mEndDate.date()) {
        m = wholeWeek / 4;
        w = 0;
      } else if (moment(mStartDate).endOf('month').date() === dayDiff) {
        m = wholeWeek / 4;
        w = 0;
      } else if (isExact) {
        m = 0;
        w = roundToNearestHalf(weekDiff);
      } else {
        w = 0;
        m = fuzzyRounding(monthDiff);
      }
    } else {
      m = 0;
      w = roundToNearestHalf(weekDiff);
    }

    displayDurations.months = m;
    displayDurations.weeks = w;
  } else if (dayDiff % 7 === 0) {
    displayDurations.weeks = dayDiff / 7;
  } else {
    displayDurations.days = dayDiff;
  }

  return displayDurations;
}

function formattedMediumDate(date1, precision1, locale) {
  const mDate = moment(date1);
  mDate.locale(locale);
  if (!date1) {
    return '';
  } else if (precision1 === 'P1Y') {
    return mDate.format('YYYY');
  } else if (precision1 === 'P1M') {
    return mDate.format('MMM YYYY');
  } else {
    return mDate.format('ll');
  }
}

function formattedLongDate(date1, precision1, locale) {
  const mDate = moment(date1);
  mDate.locale(locale);

  if (!date1) {
    return '';
  } else if (precision1 === 'P1Y') {
    return mDate.format('YYYY');
  } else if (precision1 === 'P1M') {
    return mDate.format('MMMM YYYY');
  } else {
    return mDate.format('LL');
  }
}

function convertTimestampToSimpleDate(timestamp) {
  return moment(timestamp, moment.ISO_8601).format('YYYY-MM-DD');
}

function format(d) {
  return moment(d).format('YYYY-MM-DD');
}

function getNowTimestamp() {
  return moment().format();
}

function getYear(date) {
  if (!date) {
    return '';
  }

  const mDate = moment(date);

  return mDate.year();
}

function isValidDate(locale, dateValue, dateFormat) {
  return moment(dateValue, dateFormat, locale, true).isValid();
}

function getToday() {
  const now = new Date();

  const m = now.getMonth() + 1;
  const d = now.getDate();

  return [
    now.getFullYear(),
    (m < 10 ? `0${m}` : m),
    (d < 10 ? `0${d}` : d),
  ].join('-');
}

function durationCounts(startDateData, endDateData) {
  const mEndDate = endDateData ? moment(endDateData.date) : moment();
  const mStartDate = moment(startDateData.date);
  const dateDiff = mEndDate.diff(mStartDate, 'days');
  const mDuration = moment.duration(dateDiff, 'days');
  mDuration.add(1, 'days');

  return {
    years: mDuration.asYears(),
    months: mDuration.asMonths(),
    weeks: mDuration.asWeeks(),
    days: mDuration.asDays(),
  };
}

function isGreaterThanToday(date) {
  return moment().isAfter(date);
}

function compareDatesDescending(a, b) {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return dateA > dateB ? -1 : 1;
}

function compareDateTimeDescending(a, b) {
  const aDate = moment(`${a.date} ${a.time}`);
  const bDate = moment(`${b.date} ${b.time}`);
  return aDate.isBefore(bDate) ? 1 : -1;
}

function displayTime(timeString) {
  return moment(timeString, "HH:mm:ss").format('h:mm A');
}

function getNowTime() {
  return moment().format('HH:mm:ss');
}
function breakNowTimeIntoParts(use12HourTime) {
  const nowTime = moment();
  const hours = nowTime.hours();
  const hour12Hours = hours === 0
    ? 12
    : hours > 12
      ? hours - 12
      : hours;

  return {
    hours: use12HourTime ? hour12Hours : hours,
    minutes: nowTime.minutes(),
    seconds: nowTime.seconds(),
    period: hours > 11 ? 'pm' : 'am',
  };
}

function getDateFromDaysAway(startDate, dayCount) {
  const mDate = moment(startDate);
  mDate.add(dayCount, 'days');
  return mDate.format('YYYY-MM-DD');
}

function convert12HourTimeTo24Timestamp(timeParts) {
  const numHours = Number(timeParts.hours);

  let hours = numHours;
  if (numHours === 12) {
    hours = timeParts.isAM ? 0 : numHours;
  } else if (!timeParts.isAM) {
    hours = numHours + 12;
  }

  const minutes = `00${timeParts.minutes}`.substr(-2);
  const seconds = `00${timeParts.seconds}`.substr(-2);
  return `${hours}:${minutes}:${seconds}`;
}

const DateUtil = {
  setLocale,
  breakIntoParts,
  convertPartsToInternalFormat,
  ageData,
  getMonthOptions,
  getYearOptions,
  getDayOptions,
  insureValidDateData,
  isBefore,
  isToday,
  isYesterday,
  isTomorrow,
  isInFuture,
  isGreaterThanToday,
  timeAgo,
  today,
  diff,
  fuzzyDateDurationData,
  formattedMediumDate,
  formattedLongDate,
  convertTimestampToSimpleDate,
  getNowTimestamp,
  getMaxEndDate,
  getYear,
  isValidDate,
  getToday,
  subtract,
  add,
  durationCounts,
  format,
  compareDatesDescending,
  compareDateTimeDescending,
  displayTime,
  getNowTime,
  breakNowTimeIntoParts,
  getDateFromDaysAway,
  convert12HourTimeTo24Timestamp,
};

export default DateUtil;
